import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFeedback } from "./FeedbackContext";

interface UnsavedChangesContextValue {
  /** Há alterações não salvas em alguma tela (hoje: o editor de planilha). */
  isDirty: boolean;
  /** A tela com estado "sujo" reporta aqui. */
  setDirty: (dirty: boolean) => void;
  /**
   * Executa `proceed` se for seguro sair. Se houver alterações não salvas,
   * pede confirmação; só prossegue (limpando o estado) se o usuário aceitar.
   */
  guardNavigation: (proceed: () => void) => Promise<void>;
}

const UnsavedChangesContext = createContext<
  UnsavedChangesContextValue | undefined
>(undefined);

/**
 * Guarda global de "alterações não salvas". Fica dentro do Router e do
 * FeedbackProvider para poder usar o `confirm()` já existente. Cobre:
 *  - fechar/recarregar a aba (beforeunload nativo);
 *  - navegação interna (menu do topo, botão Voltar) via `guardNavigation`.
 */
export const UnsavedChangesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isDirty, setIsDirty] = useState(false);
  const dirtyRef = useRef(false);
  const { confirm } = useFeedback();

  const setDirty = useCallback((dirty: boolean) => {
    if (dirtyRef.current === dirty) return;
    dirtyRef.current = dirty;
    setIsDirty(dirty);
  }, []);

  const guardNavigation = useCallback(
    async (proceed: () => void) => {
      if (!dirtyRef.current) {
        proceed();
        return;
      }
      const ok = await confirm({
        title: "Alterações não salvas",
        message:
          "Você tem alterações que ainda não foram salvas. Se sair agora, elas serão perdidas.",
        confirmText: "Sair sem salvar",
        cancelText: "Continuar editando",
        tone: "danger",
      });
      if (ok) {
        setDirty(false);
        proceed();
      }
    },
    [confirm, setDirty],
  );

  // beforeunload global — só bloqueia quando há alterações pendentes.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const value = useMemo<UnsavedChangesContextValue>(
    () => ({ isDirty, setDirty, guardNavigation }),
    [isDirty, setDirty, guardNavigation],
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
    </UnsavedChangesContext.Provider>
  );
};

export const useUnsavedChanges = (): UnsavedChangesContextValue => {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) {
    throw new Error(
      "useUnsavedChanges deve ser utilizado dentro de UnsavedChangesProvider",
    );
  }
  return ctx;
};
